// PR-MAY16-INVESTOR-RATES-V2 item 3 — Vehicles section of the drawer.
//
// Lists every vehicle the investor owns + the override rate (if any)
// that beats the per-investor default for each (vehicle, contract).
// Most investors will have zero overrides. The empty state is the
// success state — it confirms the per-investor default is in force
// for everyone.

import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useInvestorVehicleRates } from '@/hooks/useInvestorVehicleRates';

interface InvestorVehiclesSectionProps {
  investorId: string;
}

interface VehicleRow {
  id: string;
  code: string;
  plate_en: string;
  status: string;
}

export function InvestorVehiclesSection({ investorId }: InvestorVehiclesSectionProps) {
  const { t } = useTranslation();

  // Investor's vehicles. Keyed on investorId so refreshing one investor's
  // drawer doesn't refetch every investor's fleet.
  const vehiclesQ = useQuery({
    queryKey: ['investor-vehicles-list', investorId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items?: VehicleRow[] } | VehicleRow[]>(
        `/api/v1/vehicles?investorId=${investorId}&pageSize=200`,
      );
      return Array.isArray(data) ? data : (data.items ?? []);
    },
  });

  // Overrides (the per-vehicle table now contains ONLY rows that
  // differ from the investor's default rate).
  const overridesQ = useInvestorVehicleRates(investorId);

  if (vehiclesQ.isLoading || overridesQ.isLoading) {
    return (
      <div className="py-8 flex items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (vehiclesQ.isError) {
    return (
      <p className="text-sm text-destructive p-3 rounded bg-destructive/10 border border-destructive/30">
        {extractErrorMessage(vehiclesQ.error)}
      </p>
    );
  }

  const vehicles = vehiclesQ.data ?? [];
  const overrides = overridesQ.data ?? [];
  const overridesByVehicle = new Map<string, number>();
  for (const o of overrides) {
    overridesByVehicle.set(o.vehicle_id, (overridesByVehicle.get(o.vehicle_id) ?? 0) + 1);
  }

  if (vehicles.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic p-4 bg-muted/30 rounded border border-dashed border-border">
        {t('investors.vehiclesSection.empty')}
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden" data-investor-vehicles-section>
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="text-start px-3 py-2 font-medium">{t('investors.vehiclesSection.col.plate')}</th>
            <th className="text-start px-3 py-2 font-medium">{t('investors.vehiclesSection.col.code')}</th>
            <th className="text-start px-3 py-2 font-medium">{t('investors.vehiclesSection.col.status')}</th>
            <th className="text-end px-3 py-2 font-medium">{t('investors.vehiclesSection.col.overrides')}</th>
          </tr>
        </thead>
        <tbody>
          {vehicles.map((v) => {
            const overrideCount = overridesByVehicle.get(v.id) ?? 0;
            return (
              <tr key={v.id} className="border-t border-border/60">
                <td className="px-3 py-2 font-mono">{v.plate_en}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground font-mono">{v.code}</td>
                <td className="px-3 py-2 text-muted-foreground">{v.status}</td>
                <td className="px-3 py-2 text-end tabular-nums">
                  {overrideCount === 0 ? (
                    <span className="text-muted-foreground text-xs">{t('investors.vehiclesSection.usesDefault')}</span>
                  ) : (
                    <span className="text-amber-600 dark:text-amber-400 text-xs font-medium">
                      {t('investors.vehiclesSection.overrideCount', { count: overrideCount })}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
